/**
 * FocalDealsView — встроенный view для чат-топика «Фокусные сделки»
 * (kind='focal', slug='focal-deals'). Аналог DriveView / ReportsView.
 *
 * Excel-табличный реестр сделок, отмеченных через звёздочку в
 * «Контроль сделок» (ReportsPage). Колонки сортируемые, клик по
 * клиенту — открыть в AmoCRM, клик по ⭐ — снять с фокуса.
 */
import { crmDealUrl } from "@/lib/crmLink";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchFocalDeals, unmarkFocalDeal, patchFocalDeal, topicAvatarUrl, addDealReportNote,
  sendVoiceNote,
  type FocalDeal, type FocalDealsList, type FocalNote, type ChatTopic,
  type PatchFocalPayload, fetchReportFunnels, type ReportFunnelInfo,
} from "@/api/dashboard";
import { VoiceNoteRecorder, VoiceNotePlayer, extractVoiceAudioUrl } from "@/components/VoiceNoteRecorder";
import { Spinner } from "@/components/Spinner";
import { EditGroupModal } from "@/components/EditGroupModal";
import { useBackdropClose } from "@/lib/backdropClose";
import { parseTopicIcon } from "@/data/animatedIcons";


type SortKey = "marked_at" | "client_name" | "manager_name" | "engineer_name"
             | "region" | "visit_at" | "sum_contract" | "amo_stage" | "marked_by_name";
type SortDir = "asc" | "desc";

/** Вкладки-воронки. Беседки (кирпичные-беседки.рф) и Бани (krepkiy-par.ru) —
 *  разные бизнесы, в одном списке их смешивать нельзя. «Прочее» — гаражи,
 *  чужие воронки и сделки, удалённые в Amo; вкладка появляется, только если
 *  такие сделки есть. Воронку отдаёт бэкенд (focal_deals.funnel). */
// Воронки компании — с сервера (04.10.2026, /api/reports/funnels): у КБ Беседки и Бани, у компании brixcrm — её.
// «Прочее» и «Все» — всегда свои.
type FunnelTab = string;
type FunnelTabInfo = { key: FunnelTab; label: string; emoji: string };
const FUNNEL_EMOJI: Record<string, string> = { besedki: "🏡", bani: "🧖", other: "❔", all: "∑" };
const KB_FUNNELS: ReportFunnelInfo[] = [{ key: "besedki", name: "Беседки" }, { key: "bani", name: "Бани" }];


export function FocalDealsView({ topic, canModerate, myUserId }: {
  topic?: ChatTopic;   // без чата — страницей CRM компании brixcrm (04.10.2026)
  canModerate: boolean;
  myUserId: string | null;
}) {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const [sortKey, setSortKey] = useState<SortKey>("marked_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filter, setFilter] = useState("");
  const [managerFilter, setManagerFilter] = useState<string>("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const funnelsQ = useQuery({ queryKey: ["report-funnels"], queryFn: fetchReportFunnels, staleTime: 5 * 60_000 });
  const funnelTabs = useMemo<FunnelTabInfo[]>(() => [
    ...(funnelsQ.data ?? KB_FUNNELS).map(f => ({ key: f.key, label: f.name, emoji: FUNNEL_EMOJI[f.key] ?? "" })),
    { key: "other", label: "Прочее", emoji: FUNNEL_EMOJI.other },
    { key: "all", label: "Все", emoji: FUNNEL_EMOJI.all },
  ], [funnelsQ.data]);
  const [funnelPick, setFunnelTab] = useState<FunnelTab | null>(null);
  const funnelTab: FunnelTab = funnelPick ?? funnelTabs[0]?.key ?? "besedki";

  const q = useQuery({
    queryKey: ["focal-deals"],
    queryFn: fetchFocalDeals,
    staleTime: 30_000,
  });

  // Удаление из реестра. Оптимистично убираем строку сразу: список
  // перезапрашивается с сервера не мгновенно, и раньше клик по ★ выглядел
  // как «ничего не произошло» — сделки казались неудаляемыми. Ошибку
  // показываем явно и возвращаем строку на место.
  const unmarkMut = useMutation({
    mutationFn: (amoId: number) => unmarkFocalDeal(amoId),
    onMutate: async (amoId: number) => {
      await qc.cancelQueries({ queryKey: ["focal-deals"] });
      const prev = qc.getQueryData<FocalDealsList>(["focal-deals"]);
      if (prev) {
        qc.setQueryData<FocalDealsList>(["focal-deals"], {
          ...prev,
          items: prev.items.filter(i => i.amo_lead_id !== amoId),
          total: Math.max(0, prev.total - 1),
        });
      }
      return { prev };
    },
    onError: (e, _amoId, ctx) => {
      if (ctx?.prev) qc.setQueryData(["focal-deals"], ctx.prev);
      alert(`Не получилось убрать из фокусных: ${e instanceof Error ? e.message : String(e)}`);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["focal-deals"] });
      qc.invalidateQueries({ queryKey: ["focal-deals-ids"] });
    },
  });

  function requestUnmark(it: FocalDeal) {
    const name = (it.client_name ?? "").trim() || `#${it.amo_lead_id}`;
    const short = name.length > 60 ? `${name.slice(0, 60)}…` : name;
    if (!window.confirm(
      `Убрать «${short}» из фокусных сделок?\n\n` +
      `В CRM сделка останется — пропадёт только из этого реестра.`
    )) return;
    unmarkMut.mutate(it.amo_lead_id);
  }

  // «Обновить из Amo» больше нет (владелец 02.10.2026: «Нам нужна теперь эта кнопка?»): этап, сумма, менеджер
  // и воронка приходят из CRM при каждом открытии.

  // Сколько сделок в каждой воронке — числа на вкладках.
  const funnelCounts = useMemo(() => {
    const c: Record<FunnelTab, number> = { besedki: 0, bani: 0, other: 0, all: 0 };
    (q.data?.items ?? []).forEach(it => {
      const f = (it.funnel ?? "other") as Exclude<FunnelTab, "all">;
      c[f] = (c[f] ?? 0) + 1;
      c.all += 1;
    });
    return c;
  }, [q.data]);

  // Сделки выбранной воронки — основа и для списка, и для фильтра менеджеров,
  // и для текстового отчёта (отчёт по баням не должен тянуть беседки).
  const funnelItems = useMemo(() => {
    const items = q.data?.items ?? [];
    if (funnelTab === "all") return items;
    return items.filter(it => (it.funnel ?? "other") === funnelTab);
  }, [q.data, funnelTab]);

  // Уникальные менеджеры выбранной воронки — для dropdown фильтра.
  const managerOptions = useMemo(() => {
    const set = new Set<string>();
    funnelItems.forEach(it => {
      if (it.manager_name) set.add(it.manager_name);
    });
    return Array.from(set).sort();
  }, [funnelItems]);

  const sortedItems = useMemo(() => {
    let items = funnelItems.slice();
    if (managerFilter) {
      items = items.filter(it => it.manager_name === managerFilter);
    }
    if (filter.trim()) {
      const needle = filter.trim().toLowerCase();
      items = items.filter(it => {
        const fields = [
          it.client_name, it.manager_name, it.engineer_name,
          it.region, it.amo_stage, it.marked_by_name, it.note,
        ];
        return fields.some(f => (f ?? "").toLowerCase().includes(needle));
      });
    }
    items.sort((a, b) => {
      const av = pickSortVal(a, sortKey);
      const bv = pickSortVal(b, sortKey);
      const cmp = compareVals(av, bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return items;
  }, [funnelItems, filter, managerFilter, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  }

  return (
    <div style={{
      flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
      background: "var(--paper, #f4f4f5)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "14px 20px", borderBottom: "1px solid var(--line)",
        background: "#fff",
      }}>
        {topic && (
          <TopicHeaderTile topic={topic} fallbackEmoji="⭐"
            fallbackGradient="linear-gradient(135deg, #f9a825 0%, #ff8f00 100%)"
            onClick={canModerate ? () => setEditOpen(true) : undefined}/>
        )}
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <button
            type="button"
            onClick={() => canModerate && setEditOpen(true)}
            disabled={!canModerate}
            title={canModerate ? "Управление приложением" : ""}
            style={{
              background: "transparent", border: "none", padding: 0,
              fontSize: 15, fontWeight: 700, color: "var(--ink)",
              cursor: canModerate ? "pointer" : "default",
              textAlign: "left", fontFamily: "inherit",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {topic?.name ?? "Фокусные сделки"}
            {canModerate && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--mute)" }}>✎</span>}
          </button>
          <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 1 }}>
            {q.data
              ? `${funnelItems.length} ${pluralDeals(funnelItems.length)}`
                + (funnelTab === "all" ? "" : ` · из ${q.data.total}`)
              : "…"}
          </div>
        </div>
        {/* Воронки: Беседки / Бани — отдельными вкладками. «Прочее»
            показываем только когда там что-то есть. */}
        <div style={{ flex: "1 0 100%", display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {funnelTabs.filter(t => t.key !== "other" || funnelCounts.other > 0).map(t => {
            const active = funnelTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setFunnelTab(t.key)}
                style={{
                  padding: "6px 12px", fontSize: 13, fontFamily: "inherit",
                  fontWeight: active ? 700 : 500,
                  border: "1px solid " + (active ? "#f9a825" : "var(--line)"),
                  background: active ? "rgba(249,168,37,0.12)" : "#fafafa",
                  color: active ? "#7c5300" : "var(--ink-2)",
                  borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <span>{t.emoji}</span>
                <span>{t.label}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: active ? "#7c5300" : "var(--mute)",
                }}>{funnelCounts[t.key] ?? 0}</span>
              </button>
            );
          })}
        </div>
        <div style={{ flex: "1 0 100%", display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
          <input
            type="search"
            placeholder="Фильтр по клиенту / менеджеру / стадии…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              flex: 1, minWidth: 200, padding: "6px 10px", fontSize: 13,
              border: "1px solid var(--line)", borderRadius: 6,
              background: "#fafafa", outline: "none", fontFamily: "inherit",
            }}
          />
          <select
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            style={{
              padding: "6px 10px", fontSize: 13,
              border: "1px solid var(--line)", borderRadius: 6,
              background: "#fafafa", outline: "none", fontFamily: "inherit",
              cursor: "pointer", minWidth: 180,
            }}
            title="Фильтр по менеджеру"
          >
            <option value="">Все менеджеры</option>
            {managerOptions.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button
            type="button"
            onClick={() => setExportModalOpen(true)}
            disabled={sortedItems.length === 0}
            style={{
              padding: "6px 12px", fontSize: 13,
              background: "var(--brick, #42a5f5)", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
              whiteSpace: "nowrap", fontFamily: "inherit", fontWeight: 500,
            }}
            title="Сформировать текстовый отчёт (доступно после галочки «информация проверена»)"
          >
            📋 Текстовый отчёт
          </button>

          {canModerate && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              title="Управление приложением"
              style={{
                background: "transparent", border: "1px solid var(--line)",
                borderRadius: 6, padding: "6px 10px", fontSize: 12,
                color: "var(--ink-2)", cursor: "pointer", whiteSpace: "nowrap",
                display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg>Настроить
            </button>
          )}
        </div>
      </div>

      {q.isLoading ? (
        <div style={{ flex: 1, display: "grid", placeItems: "center" }}>
          <Spinner size="lg"/>
        </div>
      ) : !q.data || q.data.items.length === 0 ? (
        <div style={{
          flex: 1, display: "grid", placeItems: "center",
          color: "var(--mute)", padding: 40,
          fontSize: 14, textAlign: "center",
        }}>
          <div>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⭐</div>
            <div>Пока ни одной сделки не отмечено как фокусная.</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Откройте «Контроль сделок» → клик по ☆ рядом с номером сделки.
            </div>
          </div>
        </div>
      ) : sortedItems.length === 0 ? (
        <div style={{
          flex: 1, display: "grid", placeItems: "center",
          color: "var(--mute)", padding: 40,
          fontSize: 14, textAlign: "center",
        }}>
          <div>
            <div style={{ fontSize: 32, marginBottom: 8 }}>
              {funnelTabs.find(t => t.key === funnelTab)?.emoji || "⭐"}
            </div>
            <div>
              {filter.trim() || managerFilter
                ? "Под фильтр ничего не подошло."
                : `В воронке «${funnelTabs.find(t => t.key === funnelTab)?.label}» фокусных сделок нет.`}
            </div>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Всего в реестре {q.data.total} {pluralDeals(q.data.total)} — переключите вкладку воронки.
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          flex: 1, minHeight: 0, overflow: "auto",
          background: "#fff",
        }}>
          {/* Mobile: карточки, Desktop: таблица. Переключение через CSS media. */}
          <div className="focal-mobile-cards">
            {sortedItems.map(it => (
              <FocalCard
                key={it.amo_lead_id}
                it={it}
                onUnmark={() => requestUnmark(it)}
                unmarkPending={unmarkMut.isPending}
              />
            ))}
          </div>
          <table className="focal-desktop-table" style={{
            width: "100%", borderCollapse: "separate", borderSpacing: 0,
            fontSize: 13, fontVariantNumeric: "tabular-nums",
          }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <Th>★</Th>
                <Th sortKey="client_name" current={sortKey} dir={sortDir} onClick={toggleSort}>Клиент</Th>
                <Th sortKey="region" current={sortKey} dir={sortDir} onClick={toggleSort}>Регион</Th>
                <Th sortKey="manager_name" current={sortKey} dir={sortDir} onClick={toggleSort}>Менеджер</Th>
                <Th sortKey="engineer_name" current={sortKey} dir={sortDir} onClick={toggleSort}>Инженер</Th>
                <Th sortKey="visit_at" current={sortKey} dir={sortDir} onClick={toggleSort}>Дата выезда</Th>
                <Th sortKey="sum_contract" current={sortKey} dir={sortDir} onClick={toggleSort} align="right">Договор, ₽</Th>
                <Th sortKey="amo_stage" current={sortKey} dir={sortDir} onClick={toggleSort}>Этап</Th>
                <Th sortKey="marked_by_name" current={sortKey} dir={sortDir} onClick={toggleSort}>Отметил</Th>
                <Th sortKey="marked_at" current={sortKey} dir={sortDir} onClick={toggleSort}>Дата отметки</Th>
                <Th align="right">💬</Th>
                <Th>CRM</Th>
                <Th align="right">🗑</Th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map(it => (
                <FocalRow
                  key={it.amo_lead_id}
                  it={it}
                  onUnmark={() => requestUnmark(it)}
                  unmarkPending={unmarkMut.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editOpen && topic && (
        <EditGroupModal
          topic={topic}
          myUserId={myUserId}
          onClose={() => {
            setEditOpen(false);
            qc.invalidateQueries({ queryKey: ["chat-topics"] });
          }}
        />
      )}
      {exportModalOpen && (
        <ExportReportModal
          deals={sortedItems}
          managerName={managerFilter || null}
          funnelLabel={funnelTab === "all"
            ? null
            : (funnelTabs.find(t => t.key === funnelTab)?.label ?? null)}
          onClose={() => setExportModalOpen(false)}
        />
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
   FocalExportRow — одна строка в левой колонке Export-модалки.

   Показывает: имя клиента, сумму, последнее Amo-примечание (read-only),
   статус, дату следующего контакта, галочку «Проверена». Pencil-кнопка
   разворачивает inline-редактор для пользовательского note (это то, что
   реально идёт в текстовый отчёт под «Статус: ...»).
   ─────────────────────────────────────────────────────────────────── */
function FocalExportRow({ d, onPatch }: {
  d: FocalDeal;
  onPatch: (p: PatchFocalPayload) => void;
}) {
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState(d.note ?? "");

  function saveNote() {
    onPatch({ note: noteDraft.trim() || null });
    setEditingNote(false);
  }

  const isReady = d.info_verified && d.status === "potential" && d.next_contact_date;
  const inReport = d.status === "potential";

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 8,
      padding: "10px 12px",
      background: isReady ? "rgba(76,175,80,0.08)"
                : inReport ? "rgba(255,193,7,0.08)"
                : "#fafafa",
      border: "1px solid var(--line)", borderRadius: 8,
    }}>
      {/* Имя + сумма + manager */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {d.client_name || `#${d.amo_lead_id}`}
        </div>
        {(() => {
          const p = d.amo_price ?? d.sum_contract;
          if (p == null || p <= 0) return null;
          return (
            <div style={{
              fontSize: 12, fontWeight: 600, color: "#2e7d32",
              padding: "1px 8px", background: "rgba(76,175,80,0.12)", borderRadius: 4,
            }}>
              {new Intl.NumberFormat("ru-RU").format(p)} ₽
            </div>
          );
        })()}
      </div>
      <div style={{ fontSize: 11, color: "var(--mute)" }}>{d.manager_name ?? "—"}</div>

      {/* Последнее примечание из Amo (read-only) + pencil */}
      <div style={{
        display: "flex", gap: 6, alignItems: "flex-start",
        padding: "6px 8px",
        background: "#fff", border: "1px solid var(--line)",
        borderRadius: 6, fontSize: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
            {editingNote ? "Примечание (в отчёт)" : (d.note ? "Примечание (в отчёт)" : "Последнее примечание")}
          </div>
          {editingNote ? (
            <textarea
              autoFocus
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onBlur={saveNote}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveNote(); }
                if (e.key === "Escape") { setNoteDraft(d.note ?? ""); setEditingNote(false); }
              }}
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: 6, fontSize: 12, fontFamily: "inherit",
                border: "1px solid var(--tg-accent, #42a5f5)",
                borderRadius: 4, outline: "none", resize: "vertical",
              }}
              placeholder="Напишите статус сделки для отчёта…"
            />
          ) : (
            <div style={{
              color: d.note ? "var(--ink)" : "var(--mute)",
              whiteSpace: "pre-wrap", wordBreak: "break-word",
              lineHeight: 1.4,
            }}>
              {d.note || d.last_amo_note || <em style={{ color: "var(--mute-2)" }}>нет примечаний</em>}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            if (editingNote) {
              saveNote();
            } else {
              setNoteDraft(d.note ?? d.last_amo_note ?? "");
              setEditingNote(true);
            }
          }}
          title={editingNote ? "Сохранить (или нажмите вне поля)" : "Исправить примечание"}
          style={{
            border: "none", background: "transparent",
            cursor: "pointer", padding: 2, fontSize: 14,
            color: editingNote ? "#2e7d32" : "var(--mute)",
            flexShrink: 0, lineHeight: 1,
          }}
        >{editingNote ? "✓" : "✎"}</button>
      </div>

      {/* Status + date + verified */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 140px auto", gap: 6 }}>
        <select
          value={d.status ?? ""}
          onChange={(e) => onPatch({ status: e.target.value || null })}
          style={{
            padding: "6px 8px", fontSize: 12,
            border: "1px solid var(--line)", borderRadius: 6,
            background: "#fff",
          }}
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          type="date"
          value={d.next_contact_date ?? ""}
          onChange={(e) => onPatch({
            next_contact_date: e.target.value || null,
            next_contact_source: e.target.value ? "manual" : null,
          })}
          style={{
            padding: "6px 8px", fontSize: 12,
            border: "1px solid var(--line)", borderRadius: 6,
            background: "#fff", fontFamily: "inherit",
          }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", paddingLeft: 4 }}>
          <input
            type="checkbox"
            checked={d.info_verified}
            onChange={(e) => onPatch({ info_verified: e.target.checked })}
          />
          Проверена
        </label>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
   ExportReportModal — текстовый отчёт по фокусным сделкам.

   UX: список выбранного менеджера (или всех), для каждой сделки
   — статус / дата след. контакта / галочка «информация проверена».
   Кнопка «Скопировать» включается когда хотя бы 1 сделка eligible
   (status='potential' + info_verified=true + next_contact_date set).
   ─────────────────────────────────────────────────────────────────── */
const STATUS_LABEL: Record<string, string> = {
  potential: "Потенциальный договор",
  thinking: "Думает",
  rejected: "Отказ",
};
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "— не выбран —" },
  { value: "potential", label: "Потенциальный договор" },
  { value: "thinking", label: "Думает" },
  { value: "rejected", label: "Отказ" },
];

function ExportReportModal({ deals, managerName, funnelLabel, onClose }: {
  deals: FocalDeal[];
  managerName: string | null;
  /** Воронка выбранной вкладки — уходит в заголовок отчёта, чтобы
   *  отчёт по баням не читался как отчёт по беседкам. */
  funnelLabel: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // Локальный кеш изменений (сразу видны UI ещё до сетевого ответа)
  const [localOverrides, setLocalOverrides] = useState<Record<number, Partial<FocalDeal>>>({});

  const enriched = useMemo(
    () => deals.map(d => ({ ...d, ...(localOverrides[d.amo_lead_id] ?? {}) })),
    [deals, localOverrides],
  );

  const patchMut = useMutation({
    mutationFn: async (args: { amoId: number; patch: PatchFocalPayload }) =>
      patchFocalDeal(args.amoId, args.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["focal-deals"] }),
  });

  function patch(amoId: number, p: PatchFocalPayload) {
    setLocalOverrides(prev => ({ ...prev, [amoId]: { ...prev[amoId], ...p } }));
    patchMut.mutate({ amoId, patch: p });
  }

  // «В отчёт попадут» = все сделки со статусом «Потенциальный договор».
  // Превью показывает их сразу, без требования к дате/галочке.
  const inReport = enriched.filter(d => d.status === "potential");
  // Copy-ready = все «в отчёте» сделки имеют дату + галочку «проверена».
  // Если хоть одна без даты или без галочки — disable copy.
  const copyReady = inReport.length > 0 && inReport.every(d =>
    d.info_verified && !!d.next_contact_date
  );
  const missingPerDeal = inReport.filter(d => !d.info_verified || !d.next_contact_date);
  const potentialMoney = inReport.reduce((s, d) => s + (d.amo_price ?? d.sum_contract ?? 0), 0);
  const potentialDeals = inReport.length;

  const reportText = useMemo(() => {
    const lines: string[] = [];
    lines.push(funnelLabel ? `Фокусные — ${funnelLabel}` : "Фокусные");
    lines.push("");
    inReport.forEach((d, i) => {
      const num = i + 1;
      const name = (d.client_name ?? "").trim() || `Сделка #${d.amo_lead_id}`;
      const url = crmDealUrl(d.amo_lead_id) ?? "";
      lines.push(`${num}. ${name}${url ? ` ${url}` : ""}`);
      // Сумма: приоритет — actual цена из Amo (lead.price), фолбэк на focal.sum_contract.
      // Жирно — выделяем для читаемости.
      const price = d.amo_price ?? d.sum_contract;
      if (price != null && price > 0) {
        const fmt = new Intl.NumberFormat("ru-RU").format(price);
        lines.push(`Сумма: **${fmt}**`);
      }
      // Статус — категориальная метка («Потенциальный договор»), жирно.
      const statusLabel = STATUS_LABEL[d.status ?? ""] ?? d.status ?? "—";
      lines.push(`Статус: **${statusLabel}**`);
      // Последнее примечание — ОТДЕЛЬНОЙ строкой. Приоритет: пользовательский
      // note (через ✎) → Amo last_note (из снапшота).
      const noteText = (d.note ?? "").trim() || (d.last_amo_note ?? "").trim();
      if (noteText) {
        lines.push(`Последнее примечание: ${noteText}`);
      }
      if (d.next_contact_date) {
        const dt = d.next_contact_date.split("-");
        const ru = dt.length === 3 ? `${dt[2]}.${dt[1]}` : d.next_contact_date;
        lines.push(`*Дата следующего контакта:* ${ru}`);
      } else {
        lines.push("*Дата следующего контакта:* ");
      }
      lines.push("");
    });
    const moneyMln = (potentialMoney / 1_000_000).toFixed(0);
    lines.push(`**Потенциал в деньгах:** ${moneyMln} млн`);
    lines.push(`**Потенциал в договорах:** ${potentialDeals}`);
    lines.push(`**Факт:** N договоров.`);
    lines.push(`**План на месяц:** N договоров.`);
    return lines.join("\n");
  }, [inReport, potentialMoney, potentialDeals, funnelLabel]);

  const [copied, setCopied] = useState(false);
  // Закрытие по затемнению — только настоящим кликом по нему, не отпусканием мыши
  // после выделения текста (иначе «выбрасывало» из отчёта).
  const backdrop = useBackdropClose(onClose);
  function copyReport() {
    navigator.clipboard.writeText(reportText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => alert("Не удалось скопировать. Скопируй вручную из текстового поля."));
  }

  return (
    <div
      {...backdrop}
      className="focal-export-backdrop"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1280px, 100%)", height: "90vh",
          background: "#fff", borderRadius: 12,
          display: "flex", flexDirection: "column",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid var(--line)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
            📋 Текстовый отчёт{funnelLabel ? ` · ${funnelLabel}` : ""}{managerName ? ` — ${managerName}` : ""}
          </div>
          <div style={{ fontSize: 12, color: inReport.length > 0 ? "#2e7d32" : "var(--mute)" }}>
            В отчёт попадут <strong>{inReport.length}</strong> из {enriched.length}
          </div>
          <button type="button" onClick={onClose} style={{
            background: "transparent", border: "none", fontSize: 22,
            color: "var(--mute)", cursor: "pointer",
          }} aria-label="Закрыть">×</button>
        </div>

        {/* Двухколонный layout: слева — редактируемые сделки, справа — превью отчёта.
            На телефоне (<900px) — стек. */}
        <div className="focal-export-grid" style={{
          flex: 1, minHeight: 0,
          display: "grid", gridTemplateColumns: "1fr 1fr",
        }}>
          {/* ── LEFT: Editable deals list ── */}
          <div style={{
            overflow: "auto", padding: 16, borderRight: "1px solid var(--line)",
          }}>
            <div style={{ marginBottom: 12, fontSize: 12, color: "var(--mute)" }}>
              Чтобы сделка попала в отчёт: статус «Потенциальный договор» +
              дата следующего контакта + галочка «Информация проверена».
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {enriched.map(d => (
                <FocalExportRow
                  key={d.amo_lead_id}
                  d={d}
                  onPatch={(p) => patch(d.amo_lead_id, p)}
                />
              ))}
            </div>
          </div>

          {/* ── RIGHT: Live report preview ── */}
          <div style={{
            display: "flex", flexDirection: "column",
            background: "#fafafa", minHeight: 0,
          }}>
            <div style={{
              padding: "10px 16px", fontSize: 12, color: "var(--mute)",
              borderBottom: "1px solid var(--line)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span>📝 Превью отчёта</span>
              <span style={{ marginLeft: "auto", fontSize: 11 }}>
                {inReport.length === 0
                  ? "Нет сделок со статусом «Потенциальный договор» — выбери его слева"
                  : copyReady
                    ? `${inReport.length} сделок готовы · ${(potentialMoney / 1_000_000).toFixed(0)} млн`
                    : `${inReport.length} сделок · ${missingPerDeal.length} без даты/галочки`}
              </span>
            </div>
            <textarea
              readOnly
              value={reportText}
              style={{
                flex: 1, minHeight: 0,
                padding: 16, fontSize: 12.5,
                fontFamily: "ui-monospace, Menlo, monospace",
                border: "none", outline: "none",
                background: "#fafafa", color: "var(--ink)",
                resize: "none",
              }}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />
          </div>
        </div>

        <div style={{
          padding: "12px 18px", borderTop: "1px solid var(--line)",
          display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center",
        }}>
          {copied && (
            <span style={{ fontSize: 12, color: "#4caf50", marginRight: "auto" }}>
              ✓ Скопировано
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 14px", fontSize: 13,
              background: "transparent", color: "var(--ink-2)",
              border: "1px solid var(--line)", borderRadius: 6, cursor: "pointer",
            }}
          >Закрыть</button>
          <button
            type="button"
            onClick={copyReport}
            disabled={!copyReady}
            title={!copyReady && inReport.length > 0
              ? "Чтобы скопировать: у всех «Потенциальных» сделок проставь дату следующего контакта и галочку «Проверена»."
              : ""}
            style={{
              padding: "8px 16px", fontSize: 13, fontWeight: 600,
              background: !copyReady ? "#cfd8dc" : "#42a5f5",
              color: "#fff", border: "none", borderRadius: 6,
              cursor: !copyReady ? "default" : "pointer",
            }}
          >📋 Скопировать отчёт</button>
        </div>
      </div>
    </div>
  );
}


function TopicHeaderTile({ topic, fallbackEmoji, fallbackGradient, onClick }: {
  topic: ChatTopic;
  fallbackEmoji: string;
  fallbackGradient: string;
  onClick?: () => void;
}) {
  // Приоритет: 1) загруженный аватар, 2) pack/emoji-иконка топика
  // (то же, что рендерится в сайдбаре), 3) дефолт-градиент с emoji.
  const avatarUrl = topic.avatar_filename ? topicAvatarUrl(topic.id, topic.avatar_filename) : null;
  const parsedIcon = !avatarUrl ? parseTopicIcon(topic.icon) : { kind: "none" as const };

  let content: React.ReactNode = null;
  if (avatarUrl) {
    content = null;
  } else if (parsedIcon.kind === "pack") {
    content = (
      <img
        src={parsedIcon.url}
        alt=""
        draggable={false}
        style={{ width: 28, height: 28, objectFit: "contain" }}
      />
    );
  } else if (parsedIcon.kind === "emoji") {
    content = <span style={{ fontSize: 22, lineHeight: 1 }}>{parsedIcon.emoji}</span>;
  } else {
    content = <span style={{ fontSize: 22, lineHeight: 1 }}>{fallbackEmoji}</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={onClick ? "Изменить аватар" : ""}
      style={{
        width: 44, height: 44, borderRadius: 12,
        background: avatarUrl ? `url(${avatarUrl}) center/cover no-repeat` : fallbackGradient,
        border: "none", padding: 0,
        display: "grid", placeItems: "center",
        color: "#fff",
        cursor: onClick ? "pointer" : "default",
        flexShrink: 0,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      }}
    >
      {content}
    </button>
  );
}


function FocalRow({ it, onUnmark, unmarkPending }: {
  it: FocalDeal;
  onUnmark: () => void;
  unmarkPending: boolean;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [localNotes, setLocalNotes] = useState<FocalNote[]>(it.notes ?? []);

  const saveNote = useMutation({
    mutationFn: () => addDealReportNote(it.amo_lead_id, note, true, "visits"),
    onSuccess: (res) => {
      setLocalNotes(prev => [{
        id: res.note.id,
        text: res.note.text,
        author_name: res.note.author_name,
        created_at: res.note.created_at,
      }, ...prev]);
      setNote("");
      qc.invalidateQueries({ queryKey: ["focal-deals"] });
    },
    onError: (e) => alert(`Ошибка: ${e instanceof Error ? e.message : String(e)}`),
  });

  async function sendVoice(args: { file: Blob; durationSec: number }) {
    try {
      const res = await sendVoiceNote(it.amo_lead_id, args.file, args.durationSec, {
        category: "visits",
        propagateToProduction: true,
      });
      setLocalNotes(prev => [{
        id: res.note.id, text: res.note.text,
        author_name: res.note.author_name, created_at: res.note.created_at,
      }, ...prev]);
      qc.invalidateQueries({ queryKey: ["focal-deals"] });
    } catch (e) {
      alert(`Ошибка голосового: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  const handleSubmit = () => {
    if (!note.trim() || saveNote.isPending) return;
    saveNote.mutate();
  };

  function onRowClick(e: React.MouseEvent) {
    // Не разворачивать при клике на интерактив (звезда, ссылка)
    const t = e.target as HTMLElement;
    if (t.closest("a, button, textarea")) return;
    setExpanded(v => !v);
  }

  return (
    <>
      <tr
        onClick={onRowClick}
        style={{
          borderTop: "1px solid var(--line)",
          cursor: "pointer",
          background: expanded ? "#fafafa" : undefined,
          transition: "background 100ms",
        }}
        onMouseEnter={(e) => { if (!expanded) (e.currentTarget as HTMLTableRowElement).style.background = "#fafafa"; }}
        onMouseLeave={(e) => { if (!expanded) (e.currentTarget as HTMLTableRowElement).style.background = ""; }}
      >
        <Td>
          <button
            type="button"
            onClick={onUnmark}
            disabled={unmarkPending}
            title="Убрать из фокусных"
            style={{
              border: "none", background: "transparent", padding: 0,
              fontSize: 18, lineHeight: 1, color: "#f9a825", cursor: "pointer",
            }}
          >★</button>
        </Td>
        <Td>
          {it.amo_lead_id ? (
            <a href={crmDealUrl(it.amo_lead_id) ?? undefined} target="_blank" rel="noopener noreferrer"
               style={{ color: "var(--ink)", fontWeight: 500, textDecoration: "none" }}>
              {it.client_name || `#${it.amo_lead_id}`}
            </a>
          ) : (it.client_name || `#${it.amo_lead_id}`)}
          <FunnelChip it={it}/>
        </Td>
        <Td>{it.region || "—"}</Td>
        <Td>{it.manager_name || "—"}</Td>
        <Td>{it.engineer_name || "—"}</Td>
        <Td>{fmtDateTime(it.visit_at)}</Td>
        <Td align="right">{(it.sum_contract ?? it.amo_price) != null ? formatRub((it.sum_contract ?? it.amo_price) as number) : "—"}</Td>
        <Td><StageChip stage={it.amo_stage}/></Td>
        <Td>{it.marked_by_name || "—"}</Td>
        <Td>{fmtDateTime(it.marked_at, true)}</Td>
        <Td align="right">
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 12, color: localNotes.length > 0 ? "#7c5300" : "var(--mute)",
            fontWeight: localNotes.length > 0 ? 600 : 400,
          }}>
            {localNotes.length > 0 ? `💬 ${localNotes.length}` : "+"}
            <span style={{ fontSize: 9, color: "var(--mute)" }}>{expanded ? "▲" : "▼"}</span>
          </span>
        </Td>
        <Td>
          {!!it.amo_lead_id && (
            <a href={crmDealUrl(it.amo_lead_id) ?? undefined} target="_blank" rel="noopener noreferrer"
               style={{ color: "var(--mute)", textDecoration: "none", fontSize: 11 }}>↗</a>
          )}
        </Td>
        <Td align="right">
          <button
            type="button"
            onClick={onUnmark}
            disabled={unmarkPending}
            title="Удалить из фокусных сделок"
            style={{
              border: "none", background: "transparent", padding: "2px 4px",
              fontSize: 14, lineHeight: 1, cursor: "pointer",
              color: "var(--mute)", opacity: unmarkPending ? 0.4 : 1,
            }}
          >🗑</button>
        </Td>
      </tr>

      {expanded && (
        <tr style={{ background: "#fafafa" }}>
          <td colSpan={13} style={{ padding: 0, borderBottom: "1px solid var(--line)" }}>
            <div style={{
              padding: "14px 20px 18px 20px",
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              {/* Заголовок секции */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                fontSize: 11, fontWeight: 600, color: "var(--mute)",
                textTransform: "uppercase", letterSpacing: 0.4,
              }}>
                <span>Примечания</span>
                {localNotes.length > 0 && (
                  <span style={{
                    padding: "1px 7px", borderRadius: 999,
                    background: "#fff8e1", color: "#7c5300",
                    fontSize: 10, fontWeight: 700,
                  }}>{localNotes.length}</span>
                )}
              </div>

              {/* Список примечаний */}
              {localNotes.length > 0 && (
                <div style={{
                  display: "flex", flexDirection: "column",
                  background: "#fff",
                  border: "1px solid var(--line)", borderRadius: 8,
                  overflow: "hidden",
                }}>
                  {localNotes.map((n, i) => (
                    <div key={n.id} style={{
                      display: "flex", gap: 10, padding: "10px 12px",
                      borderTop: i === 0 ? "none" : "1px solid var(--line)",
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 14,
                        background: colorFromName(n.author_name || "?"),
                        color: "#fff", fontSize: 11, fontWeight: 700,
                        display: "grid", placeItems: "center",
                        flexShrink: 0, lineHeight: 1,
                      }}>
                        {initialsOf(n.author_name || "?")}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: "flex", alignItems: "baseline", gap: 8,
                          fontSize: 12, marginBottom: 2,
                        }}>
                          <span style={{ fontWeight: 600, color: "var(--ink)" }}>{n.author_name || "—"}</span>
                          <span style={{ color: "var(--mute)", fontSize: 11 }}>{fmtDateTime(n.created_at, false)}</span>
                        </div>
                        <div style={{
                          fontSize: 13, color: "var(--ink-2, #444)",
                          whiteSpace: "pre-wrap", lineHeight: 1.45,
                        }}>{n.text}</div>
                        {(() => {
                          const audio = extractVoiceAudioUrl(n.text);
                          return audio ? <VoiceNotePlayer src={audio}/> : null;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Композер */}
              <div style={{
                display: "flex", gap: 6, alignItems: "stretch",
                background: "#fff",
                border: "1px solid var(--line)", borderRadius: 8,
                padding: 4,
              }}>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Новое примечание — уйдёт в ленту сделки и в карточку производства"
                  rows={1}
                  style={{
                    flex: 1, resize: "none", minHeight: 34, fontSize: 13,
                    padding: "8px 10px",
                    border: "none", background: "transparent",
                    fontFamily: "inherit", outline: "none",
                    lineHeight: 1.4,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <div onClick={(e) => e.stopPropagation()} style={{ alignSelf: "flex-end", padding: "2px 4px 2px 0" }}>
                  <VoiceNoteRecorder onSend={sendVoice} disabled={saveNote.isPending} accent="#f9a825"/>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleSubmit(); }}
                  disabled={!note.trim() || saveNote.isPending}
                  title="Отправить (⌘/Ctrl + Enter)"
                  style={{
                    width: 34, height: 34, alignSelf: "flex-end",
                    background: note.trim() && !saveNote.isPending ? "#f9a825" : "transparent",
                    color: note.trim() && !saveNote.isPending ? "#fff" : "var(--mute)",
                    border: "none", borderRadius: 6,
                    cursor: note.trim() && !saveNote.isPending ? "pointer" : "default",
                    fontSize: 16, lineHeight: 1, display: "grid", placeItems: "center",
                    flexShrink: 0,
                    transition: "background 120ms, color 120ms",
                  }}
                >{saveNote.isPending ? "…" : "➤"}</button>
              </div>
              <div style={{
                fontSize: 11, color: "var(--mute)", textAlign: "right",
                marginTop: -6,
              }}>
                ⌘/Ctrl + Enter — отправить
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


/** Метка воронки Amo у клиента. На вкладке одной воронки она не нужна,
 *  но список «Все» и вкладка «Прочее» без неё нечитаемы: там рядом лежат
 *  беседки, бани и сделки, которых уже нет в Amo. */
function FunnelChip({ it, inline }: { it: FocalDeal; inline?: boolean }) {
  const label = it.pipeline_name || "воронка не определена";
  const tone = it.funnel === "bani"
    ? { bg: "rgba(2,136,209,0.10)", fg: "#01579b" }
    : it.funnel === "besedki"
      ? { bg: "rgba(76,175,80,0.10)", fg: "#2e7d32" }
      : { bg: "rgba(0,0,0,0.05)", fg: "var(--mute)" };
  const chip = (
    <span style={{
      display: "inline-block", padding: "1px 7px", borderRadius: 999,
      background: tone.bg, color: tone.fg,
      fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
    }} title={`Воронка AmoCRM: ${label}`}>{label}</span>
  );
  if (inline) return chip;
  return <div style={{ marginTop: 3 }}>{chip}</div>;
}


/**
 * FocalCard — карточный вариант FocalRow для мобильных экранов.
 * Рендерит ту же информацию, что таблица, но в стек-формате.
 * Логика (save/voice/expand) повторяет FocalRow.
 */
function FocalCard({ it, onUnmark, unmarkPending }: {
  it: FocalDeal;
  onUnmark: () => void;
  unmarkPending: boolean;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [localNotes, setLocalNotes] = useState<FocalNote[]>(it.notes ?? []);

  const saveNote = useMutation({
    mutationFn: () => addDealReportNote(it.amo_lead_id, note, true, "visits"),
    onSuccess: (res) => {
      setLocalNotes(prev => [{
        id: res.note.id,
        text: res.note.text,
        author_name: res.note.author_name,
        created_at: res.note.created_at,
      }, ...prev]);
      setNote("");
      qc.invalidateQueries({ queryKey: ["focal-deals"] });
    },
    onError: (e) => alert(`Ошибка: ${e instanceof Error ? e.message : String(e)}`),
  });

  async function sendVoice(args: { file: Blob; durationSec: number }) {
    try {
      const res = await sendVoiceNote(it.amo_lead_id, args.file, args.durationSec, {
        category: "visits", propagateToProduction: true,
      });
      setLocalNotes(prev => [{
        id: res.note.id, text: res.note.text,
        author_name: res.note.author_name, created_at: res.note.created_at,
      }, ...prev]);
      qc.invalidateQueries({ queryKey: ["focal-deals"] });
    } catch (e) {
      alert(`Ошибка голосового: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  const handleSubmit = () => {
    if (!note.trim() || saveNote.isPending) return;
    saveNote.mutate();
  };

  return (
    <div className="focal-card" style={{
      borderBottom: "1px solid var(--line)",
      padding: "14px 14px 12px",
      background: "#fff",
    }}>
      {/* Шапка: ★ + клиент + ↗ */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <button
          type="button"
          onClick={onUnmark}
          disabled={unmarkPending}
          title="Убрать из фокусных"
          style={{
            border: "none", background: "transparent", padding: 0,
            fontSize: 22, lineHeight: 1, color: "#f9a825",
            cursor: "pointer", flexShrink: 0,
          }}
        >★</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: "var(--ink)" }}>
            {it.amo_lead_id ? (
              <a href={crmDealUrl(it.amo_lead_id) ?? undefined} target="_blank" rel="noopener noreferrer"
                 style={{ color: "var(--ink)", textDecoration: "none" }}>
                {it.client_name || `#${it.amo_lead_id}`}
              </a>
            ) : (it.client_name || `#${it.amo_lead_id}`)}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <StageChip stage={it.amo_stage}/>
            {it.region && (
              <span style={{ fontSize: 12, color: "var(--mute)" }}>📍 {it.region}</span>
            )}
            <FunnelChip it={it} inline/>
          </div>
        </div>
        {!!it.amo_lead_id && (
          <a href={crmDealUrl(it.amo_lead_id) ?? undefined} target="_blank" rel="noopener noreferrer"
             style={{ color: "var(--mute)", textDecoration: "none", fontSize: 16, flexShrink: 0 }}
             title="Открыть сделку в CRM">↗</a>
        )}
        <button
          type="button"
          onClick={onUnmark}
          disabled={unmarkPending}
          title="Удалить из фокусных сделок"
          style={{
            border: "none", background: "transparent", padding: "0 0 0 6px",
            fontSize: 17, lineHeight: 1, cursor: "pointer",
            color: "var(--mute)", flexShrink: 0, opacity: unmarkPending ? 0.4 : 1,
          }}
        >🗑</button>
      </div>

      {/* Сетка 2-col: метаданные */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr",
        gap: "10px 12px", marginTop: 12,
      }}>
        <FocalField label="Менеджер" value={it.manager_name}/>
        <FocalField label="Инженер" value={it.engineer_name}/>
        <FocalField label="Дата выезда" value={fmtDateTime(it.visit_at)}/>
        <FocalField label="Договор, ₽" value={it.sum_contract != null ? formatRub(it.sum_contract) : "—"}/>
        <FocalField label="Отметил" value={it.marked_by_name}/>
        <FocalField label="Дата отметки" value={fmtDateTime(it.marked_at, true)}/>
      </div>

      {/* Toggle примечаний */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        style={{
          marginTop: 10, width: "100%", border: "none",
          background: localNotes.length > 0 ? "#fff8e1" : "var(--paper-2, #f4f4f5)",
          color: localNotes.length > 0 ? "#7c5300" : "var(--ink-2)",
          padding: "10px 12px", borderRadius: 8,
          fontWeight: 600, fontSize: 13, cursor: "pointer",
          fontFamily: "inherit",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        }}
      >
        <span>{localNotes.length > 0 ? `💬 ${localNotes.length} ${pluralNotes(localNotes.length)}` : "💬 Добавить примечание"}</span>
        <span style={{ fontSize: 10 }}>{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div style={{
          marginTop: 10, padding: 10, background: "#fafafa",
          borderRadius: 8, display: "flex", flexDirection: "column", gap: 10,
        }}>
          {localNotes.length > 0 && (
            <div style={{
              background: "#fff", border: "1px solid var(--line)",
              borderRadius: 8, overflow: "hidden",
            }}>
              {localNotes.map((n, i) => (
                <div key={n.id} style={{
                  display: "flex", gap: 10, padding: "10px 12px",
                  borderTop: i === 0 ? "none" : "1px solid var(--line)",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 14,
                    background: colorFromName(n.author_name || "?"),
                    color: "#fff", fontSize: 11, fontWeight: 700,
                    display: "grid", placeItems: "center",
                    flexShrink: 0, lineHeight: 1,
                  }}>
                    {initialsOf(n.author_name || "?")}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: "flex", alignItems: "baseline", gap: 8,
                      fontSize: 12, marginBottom: 2, flexWrap: "wrap",
                    }}>
                      <span style={{ fontWeight: 600, color: "var(--ink)" }}>{n.author_name || "—"}</span>
                      <span style={{ color: "var(--mute)", fontSize: 11 }}>{fmtDateTime(n.created_at, false)}</span>
                    </div>
                    <div style={{
                      fontSize: 13, color: "var(--ink-2, #444)",
                      whiteSpace: "pre-wrap", lineHeight: 1.45,
                    }}>{n.text}</div>
                    {(() => {
                      const audio = extractVoiceAudioUrl(n.text);
                      return audio ? <VoiceNotePlayer src={audio}/> : null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Composer */}
          <div style={{
            display: "flex", gap: 6, alignItems: "stretch",
            background: "#fff", border: "1px solid var(--line)",
            borderRadius: 8, padding: 4,
          }}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Новое примечание…"
              rows={1}
              style={{
                flex: 1, resize: "none", minHeight: 34, fontSize: 16,
                padding: "8px 10px", border: "none", background: "transparent",
                fontFamily: "inherit", outline: "none", lineHeight: 1.4,
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <div style={{ alignSelf: "flex-end", padding: "2px 4px 2px 0" }}>
              <VoiceNoteRecorder onSend={sendVoice} disabled={saveNote.isPending} accent="#f9a825"/>
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!note.trim() || saveNote.isPending}
              title="Сохранить"
              style={{
                width: 38, height: 38, alignSelf: "flex-end",
                background: note.trim() && !saveNote.isPending ? "#f9a825" : "transparent",
                color: note.trim() && !saveNote.isPending ? "#fff" : "var(--mute)",
                border: "none", borderRadius: 6,
                cursor: note.trim() && !saveNote.isPending ? "pointer" : "default",
                fontSize: 18, lineHeight: 1, display: "grid", placeItems: "center",
                flexShrink: 0,
              }}
            >{saveNote.isPending ? "…" : "✓"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FocalField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontSize: 10.5, color: "var(--mute)", textTransform: "uppercase",
        letterSpacing: 0.4, fontWeight: 600, marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 13, color: "var(--ink)",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {value || "—"}
      </div>
    </div>
  );
}

function pluralNotes(n: number): string {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "примечание";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "примечания";
  return "примечаний";
}


function Th({ children, sortKey, current, dir, onClick, align }: {
  children: React.ReactNode;
  sortKey?: SortKey;
  current?: SortKey;
  dir?: SortDir;
  onClick?: (k: SortKey) => void;
  align?: "left" | "right";
}) {
  const sortable = !!sortKey && !!onClick;
  const active = sortable && current === sortKey;
  return (
    <th
      onClick={sortable ? () => onClick!(sortKey!) : undefined}
      style={{
        padding: "10px 12px",
        textAlign: align ?? "left",
        fontSize: 11, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: 0.4,
        color: active ? "var(--ink)" : "var(--mute)",
        background: "#fafafa", borderBottom: "1px solid var(--line)",
        cursor: sortable ? "pointer" : "default",
        userSelect: "none", whiteSpace: "nowrap",
      }}
    >
      {children}
      {active && (
        <span style={{ marginLeft: 4, fontSize: 10 }}>{dir === "asc" ? "▲" : "▼"}</span>
      )}
    </th>
  );
}


function Td({ children, align }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <td style={{
      padding: "10px 12px",
      textAlign: align ?? "left",
      color: "var(--ink-2)",
      whiteSpace: "nowrap",
      maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis",
    }}>
      {children}
    </td>
  );
}


function StageChip({ stage }: { stage: string | null }) {
  if (!stage) return <span style={{ color: "var(--mute)" }}>—</span>;
  return (
    <span style={{
      padding: "1px 8px", fontSize: 11, borderRadius: 10,
      background: "var(--paper-2, #f0f0f0)", color: "var(--ink-3, #444)",
      border: "1px solid var(--line)",
    }}>{stage}</span>
  );
}


function pickSortVal(d: FocalDeal, k: SortKey): string | number {
  switch (k) {
    case "sum_contract": return d.sum_contract ?? -1;
    case "marked_at":    return d.marked_at ?? "";
    case "visit_at":     return d.visit_at ?? "";
    default:             return (d[k] as string | null) ?? "";
  }
}

function compareVals(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "ru");
}

function fmtDateTime(s: string | null, withSec = false): string {
  if (!s) return "—";
  try {
    const d = new Date(s.replace(" ", "T") + (s.includes("Z") || s.includes("+") ? "" : "Z"));
    if (Number.isNaN(d.getTime())) return s.slice(0, 16);
    return d.toLocaleString("ru", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
      ...(withSec ? { second: "2-digit" } : {}),
    });
  } catch {
    return s.slice(0, 16);
  }
}

function formatRub(n: number): string {
  return new Intl.NumberFormat("ru-RU").format(n) + " ₽";
}

function pluralDeals(n: number): string {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "сделка";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "сделки";
  return "сделок";
}

const AVATAR_PALETTE = [
  "#4fae4e", "#3390ec", "#e99a12", "#9c7ce5", "#e85c5c", "#00acc1",
  "#7b8a52", "#d96a6a", "#5a8dee", "#b070b0",
];

function colorFromName(name: string): string {
  const s = name || "?";
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

function initialsOf(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
